import { ATTR_PREFIX } from './contract';
import type { SpeechRate } from './prefs';
import { STRINGS } from './strings';
import {
  collectTextRuns,
  isBlockDisplay,
  ownText,
  type TextBlock,
} from './text-blocks';

/**
 * Read aloud (ticket §3, D16). The browser's own speech engine
 * (`window.speechSynthesis`) reads the page out one block at a time, with the
 * block outlined and scrolled into view.
 *
 * It is not a screen reader and is never called one (ticket §9): it does not
 * announce structure, controls or state, and people who use a screen reader
 * already have their own, which this must never get in the way of.
 *
 * Only voices on the device (`localService`), so no page text is sent to a
 * speech service. It reads the page as soon as the visitor turns it on, and
 * again on each page while it is on, where the browser allows a page to talk
 * before the visitor has clicked (ticket §6). Pointing at text reads it when
 * the page is not already being read.
 */

/** On the block being read. css/main.css draws the outline. */
export const READING_ATTR = `${ATTR_PREFIX}reading`;

export interface SpeechItem {
  /** The block to outline, or null for a selection (already highlighted). */
  el: HTMLElement | null;
  text: string;
  lang: string;
}

export function speechSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.speechSynthesis &&
    !!(window as any).SpeechSynthesisUtterance
  );
}

// ---- what to read ------------------------------------------------------------

/** Site header, navigation, sidebars and footer: skipped by Read page. */
const PAGE_CHROME =
  'header, nav, footer, aside, [role="banner"], [role="navigation"], ' +
  '[role="contentinfo"], [role="complementary"]';

function mainOf(doc: Document): Element | null {
  return doc.querySelector('main, [role="main"]');
}

function langOf(el: Element | null): string {
  const holder = el ? el.closest('[lang]') : null;
  return holder ? (holder.getAttribute('lang') || '').trim() : '';
}

/**
 * Read page: the main content when the page marks it (Edlio themes do), else
 * the whole body minus header, navigation, sidebars and footer.
 */
export function pageItems(doc: Document): SpeechItem[] {
  const main = mainOf(doc);
  const root = main || doc.body;
  return root ? itemsIn(root, !main) : [];
}

/**
 * Click to read: everything from the clicked block on. Inside the main
 * content that is the rest of the main content; elsewhere, the rest of the
 * page. Null when the click was not on a block of text.
 */
export function itemsFrom(
  doc: Document,
  target: Element,
): { items: SpeechItem[]; start: number } | null {
  const main = mainOf(doc);
  const root = main && main.contains(target) ? main : doc.body;
  if (!root) return null;
  const items = itemsIn(root, false);
  const first = new Map<Element, number>();
  items.forEach((item, i) => {
    if (item.el && !first.has(item.el)) first.set(item.el, i);
  });
  for (let el: Element | null = target; el; el = el.parentElement) {
    const start = first.get(el);
    if (start !== undefined) return { items, start };
    if (el === root) break;
  }
  return null;
}

/** The visitor's selection on the page, if there is one. */
export function selectionItems(doc: Document): SpeechItem[] | null {
  const sel =
    typeof doc.getSelection === 'function' ? doc.getSelection() : null;
  if (!sel || sel.isCollapsed) return null;
  const text = sel.toString().replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const anchor = sel.anchorNode;
  const el =
    anchor && anchor.nodeType === 1
      ? (anchor as Element)
      : anchor && anchor.parentElement;
  return [{ el: null, text, lang: langOf(el) }];
}

/** Links, buttons and labels: read by their own words when pointed at. */
const NAMED_CONTROL =
  'a[href], button, summary, label, [role="button"], [role="link"], [role="tab"], [role="menuitem"]';

/**
 * What the pointer is resting on: a link or button by its name, an image by
 * its description, or else the block of text under it. Null for form fields,
 * the widget and empty space.
 */
export function pointItem(target: Element): SpeechItem | null {
  if (target.closest('input, textarea, select, [contenteditable="true"]'))
    return null;
  const control = target.closest(NAMED_CONTROL) as HTMLElement | null;
  if (control && isShown(control)) {
    const text = nameOf(control);
    return text ? { el: control, text, lang: langOf(control) } : null;
  }
  if (target.tagName === 'IMG') {
    const alt = (target.getAttribute('alt') || '').trim();
    return alt
      ? {
          el: target as HTMLElement,
          text: `${STRINGS.readImage}: ${alt}`,
          lang: langOf(target),
        }
      : null;
  }
  // Up to the box that lays out this text, then just that box's own runs.
  let block: Element | null = target;
  while (
    block &&
    block !== document.body &&
    !isBlockDisplay(window.getComputedStyle(block).display)
  )
    block = block.parentElement;
  if (!block || block === document.body || !isShown(block as HTMLElement))
    return null;
  const text = runText({ el: block as HTMLElement, parts: ownText(block) });
  return text ? { el: block as HTMLElement, text, lang: langOf(block) } : null;
}

/** A control's accessible words: aria-label, else its visible text, else an image's alt or the title. */
function nameOf(el: HTMLElement): string {
  const label = (el.getAttribute('aria-label') || '').trim();
  if (label) return label;
  let text = '';
  const walker = document.createTreeWalker(el, 1 | 4);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const owner = node.nodeType === 3 ? node.parentElement : (node as Element);
    if (owner && owner.closest('[aria-hidden="true"]')) continue;
    if (node.nodeType === 3) text += node.nodeValue || '';
    else if ((node as Element).tagName === 'IMG')
      text += ` ${(node as Element).getAttribute('alt') || ''} `;
  }
  text = text.replace(/\s+/g, ' ').trim();
  return text || (el.getAttribute('title') || '').trim();
}

function itemsIn(root: Element, skipChrome: boolean): SpeechItem[] {
  const items: SpeechItem[] = [];
  collectTextRuns(root).forEach((run) => {
    const el = run.el;
    if (skipChrome && isChrome(el, root)) return;
    if (!isShown(el)) return;
    const text = runText(run);
    if (text) items.push({ el, text, lang: langOf(el) });
  });
  return items;
}

/** In the site's header, navigation, sidebar or footer (not an article's own header). */
function isChrome(el: Element, root: Element): boolean {
  const hit = el.closest(PAGE_CHROME);
  if (!hit || hit === root || !root.contains(hit)) return false;
  if (hit.tagName === 'HEADER' || hit.tagName === 'FOOTER') {
    const parent = hit.parentElement;
    if (parent && parent.closest('article, section')) return false;
  }
  return true;
}

/** On screen for a sighted visitor: skips skip links and other 1px text. */
function isShown(el: HTMLElement): boolean {
  if (el.closest('[aria-hidden="true"], [hidden]')) return false;
  if (el.getClientRects().length === 0) return false;
  const box = el.getBoundingClientRect();
  if (box.width <= 1 || box.height <= 1) return false;
  return window.getComputedStyle(el).visibility !== 'hidden';
}

function runText(run: TextBlock): string {
  let out = '';
  let words = false;
  run.parts.forEach((part) => {
    const owner = part.nodeType === 3 ? part.parentElement : (part as Element);
    if (owner && owner.closest('[aria-hidden="true"]')) return;
    if (part.nodeType === 3) {
      out += part.nodeValue || '';
      words = true;
    } else {
      out += ` ${(part as HTMLImageElement).getAttribute('alt') || ''} `;
    }
  });
  out = out.replace(/\s+/g, ' ').trim();
  return out && !words ? `${STRINGS.readImage}: ${out}` : out;
}

// ---- chunks and voices -------------------------------------------------------

/**
 * Some engines stop partway through a long utterance, and pausing restarts
 * the current one, so text goes out in pieces of a sentence or a few.
 */
const MAX_CHUNK = 220;

export function chunkText(text: string): string[] {
  const sentences = text.match(/[^.!?…]+[.!?…]*["')\]]*\s*/g) || [text];
  const out: string[] = [];
  let current = '';
  sentences.forEach((sentence) => {
    if (current && (current + sentence).length > MAX_CHUNK) {
      out.push(current.trim());
      current = '';
    }
    let rest = sentence;
    // A run-on with no full stop: cut at the last space before the limit.
    while (rest.length > MAX_CHUNK) {
      let cut = rest.lastIndexOf(' ', MAX_CHUNK);
      if (cut < MAX_CHUNK / 2) cut = MAX_CHUNK;
      out.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut);
    }
    current += rest;
  });
  if (current.trim()) out.push(current.trim());
  return out;
}

/** macOS ships joke voices under real languages. Never pick one of those. */
const NOVELTY =
  /^(Albert|Bad News|Bahh|Bells|Boing|Bubbles|Cellos|Deranged|Good News|Hysterical|Jester|Organ|Pipe Organ|Superstar|Trinoids|Whisper|Wobble|Zarvox)\b/;

const normLang = (lang: string) => lang.replace('_', '-').toLowerCase();

/**
 * The best on-device voice for a language: same language first, then the
 * device's default voice, the exact region, and the visitor's own region.
 */
export function pickVoice(
  voices: SpeechSynthesisVoice[],
  lang: string,
): SpeechSynthesisVoice | null {
  const want = normLang(lang || 'en');
  const base = want.split('-')[0];
  const own = normLang(
    (typeof navigator !== 'undefined' && navigator.language) || '',
  );
  let best: SpeechSynthesisVoice | null = null;
  let bestScore = -Infinity;
  voices.forEach((voice) => {
    if (!voice.localService) return;
    const vl = normLang(voice.lang || '');
    if (vl.split('-')[0] !== base) return;
    const score =
      (voice.default ? 4 : 0) +
      (vl === want ? 2 : 0) +
      (vl === own ? 1 : 0) -
      (NOVELTY.test(voice.name) ? 10 : 0);
    if (score > bestScore) {
      best = voice;
      bestScore = score;
    }
  });
  return best;
}

// ---- the reader ----------------------------------------------------------------

export type ReaderState = 'idle' | 'playing' | 'paused';
/** `page`: the page, from a click or a selection. `point`: one thing under the pointer. */
export type ReadKind = 'page' | 'point';

export interface ReadOptions {
  kind?: ReadKind;
  /**
   * Started without a click on this page (page load). Browsers may refuse
   * that; if speech has not begun within START_WAIT_MS, or the engine says
   * "not-allowed", the reader stops and reports `blocked()`.
   */
  auto?: boolean;
}

export interface ReaderListener {
  state(state: ReaderState): void;
  /** The block now being read, or null when reading stops. */
  block(el: HTMLElement | null): void;
  /** Something to tell the visitor, or '' to clear it. */
  message(text: string): void;
  /** The browser would not let the page start talking before a click. */
  blocked(): void;
}

interface Chunk {
  item: number;
  text: string;
}

/** How long to wait for a browser that loads its voice list late (Chrome). */
const VOICES_WAIT_MS = 1500;
/** How long an automatic start may take to begin speaking before it counts as blocked. */
const START_WAIT_MS = 2500;

/**
 * Speaks a list of items, one chunk at a time. Pause is "stop and remember
 * where": the engines' own pause() is unreliable (Chrome on Android ends the
 * utterance instead), so resume starts the current chunk again. Every
 * utterance carries a generation number, so a callback from one that was
 * cancelled never moves the reader on.
 */
export class Reader {
  private readonly synth: SpeechSynthesis;
  private readonly listener: ReaderListener;
  private items: SpeechItem[] = [];
  private chunks: Chunk[] = [];
  private pos = 0;
  private gen = 0;
  private current: SpeechSynthesisUtterance | null = null;
  private status: ReaderState = 'idle';
  private rate: SpeechRate = 1;
  private readKind: ReadKind = 'page';
  private autoStart = false;
  private watchdog = 0;

  constructor(listener: ReaderListener) {
    this.synth = window.speechSynthesis;
    this.listener = listener;
  }

  get state(): ReaderState {
    return this.status;
  }

  get kind(): ReadKind {
    return this.readKind;
  }

  /** Chrome fills the voice list on first ask; ask early so Read page starts at once. */
  warm(): void {
    this.synth.getVoices();
  }

  read(items: SpeechItem[], from = 0, options: ReadOptions = {}): void {
    this.halt();
    this.readKind = options.kind || 'page';
    this.autoStart = !!options.auto;
    const chunks: Chunk[] = [];
    items.forEach((item, i) =>
      chunkText(item.text).forEach((text) => chunks.push({ item: i, text })),
    );
    let start = 0;
    while (start < chunks.length && chunks[start].item < from) start++;
    if (start >= chunks.length) {
      this.stop();
      return;
    }
    this.items = items;
    this.chunks = chunks;
    this.pos = start;
    this.listener.message('');
    if (this.synth.getVoices().length) {
      // Straight away, inside the click: iOS only lets a tap start speech.
      this.speak();
      return;
    }
    this.setState('playing');
    const gen = this.gen;
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      if (typeof this.synth.removeEventListener === 'function')
        this.synth.removeEventListener('voiceschanged', go);
      if (gen === this.gen && this.status === 'playing') this.speak();
    };
    const timer = window.setTimeout(go, VOICES_WAIT_MS);
    if (typeof this.synth.addEventListener === 'function')
      this.synth.addEventListener('voiceschanged', go);
  }

  pause(): void {
    if (this.status !== 'playing') return;
    this.halt();
    this.setState('paused');
  }

  resume(): void {
    if (this.status === 'paused') this.speak();
  }

  stop(): void {
    this.halt();
    this.items = [];
    this.chunks = [];
    this.pos = 0;
    this.listener.block(null);
    this.setState('idle');
  }

  setRate(rate: SpeechRate): void {
    if (rate === this.rate) return;
    this.rate = rate;
    // Engines read the rate once per utterance: restart this one at the new speed.
    if (this.status === 'playing' && this.current) {
      this.halt();
      this.speak();
    }
  }

  private halt(): void {
    this.gen++;
    this.current = null;
    this.clearWatchdog();
    this.synth.cancel();
  }

  private clearWatchdog(): void {
    if (this.watchdog) window.clearTimeout(this.watchdog);
    this.watchdog = 0;
  }

  private refused(): void {
    this.autoStart = false;
    this.stop();
    this.listener.blocked();
  }

  private speak(): void {
    const chunk = this.chunks[this.pos];
    if (!chunk) {
      this.stop();
      return;
    }
    const item = this.items[chunk.item];
    const voices = this.synth.getVoices();
    const voice =
      pickVoice(voices, item.lang || pageLang()) ||
      pickVoice(voices, pageLang());
    if (!voice) {
      this.stop();
      this.listener.message(STRINGS.readNoVoice);
      return;
    }

    const gen = ++this.gen;
    const utterance = new SpeechSynthesisUtterance(chunk.text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = this.rate;
    utterance.onstart = () => {
      if (gen !== this.gen) return;
      // It is talking, so the page is allowed to: no more watching.
      this.autoStart = false;
      this.clearWatchdog();
    };
    utterance.onend = () => {
      if (gen !== this.gen) return;
      this.pos++;
      this.speak();
    };
    utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
      if (gen !== this.gen) return;
      const error = event && event.error;
      if (error === 'interrupted' || error === 'canceled') return;
      if (error === 'not-allowed') {
        this.refused();
        return;
      }
      // One bad chunk (an engine hiccup) skips ahead instead of stopping.
      this.pos++;
      this.speak();
    };
    // Held, so Chrome cannot garbage-collect it mid-sentence and drop onend.
    this.current = utterance;
    this.listener.block(item.el);
    this.setState('playing');
    if (this.autoStart && !this.watchdog) {
      // Safari can refuse silently: no error, no start. Treat silence as no.
      this.watchdog = window.setTimeout(() => {
        this.watchdog = 0;
        if (gen === this.gen && this.autoStart) this.refused();
      }, START_WAIT_MS);
    }
    this.synth.speak(utterance);
  }

  private setState(next: ReaderState): void {
    if (next === this.status) return;
    this.status = next;
    this.listener.state(next);
  }
}

function pageLang(): string {
  return langOf(document.documentElement) || 'en';
}
